import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Database, Loader2, Sparkles, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface ChatMessageProps {
  type: 'human' | 'ai';
  content: string;
  isStreaming?: boolean;
  /** Callback to trigger patient data sync from inline button. */
  onSyncPatient?: () => Promise<void>;
}

/**
 * Detect if an AI message is asking the user to manually sync patient data.
 * Matches variations of "Force Sync", "patient data is not ready",
 * "Open the patient's IntelliHealth page", etc.
 */
function needsSyncAction(content: string): boolean {
  const lower = content.toLowerCase();
  return (
    lower.includes('force sync') ||
    lower.includes('patient data is not ready') ||
    lower.includes('patient data not ready') ||
    lower.includes('data is not ready yet') ||
    (lower.includes('intellihealth') && lower.includes('sync'))
  );
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  type,
  content,
  isStreaming = false,
  onSyncPatient,
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  const handleSync = async () => {
    if (!onSyncPatient || isSyncing || syncDone) return;
    setIsSyncing(true);
    try {
      await onSyncPatient();
      setSyncDone(true);
    } catch {
      // Toast will be shown by the parent handler
    } finally {
      setIsSyncing(false);
    }
  };

  if (type === 'human') {
    return (
      <div className="flex gap-2 justify-end">
        <div className="max-w-[85%] rounded-xl px-3 py-2 text-sm bg-brand-teal text-white">
          <p className="whitespace-pre-wrap">{content}</p>
        </div>
        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
          <User className="w-3 h-3 text-slate-500" />
        </div>
      </div>
    );
  }

  const showSyncButton = !isStreaming && onSyncPatient && needsSyncAction(content);

  return (
    <div className="flex gap-2 justify-start">
      <div className="w-6 h-6 rounded-full bg-brand-teal-light flex items-center justify-center flex-shrink-0 mt-0.5">
        <Sparkles className="w-3 h-3 text-brand-teal" />
      </div>
      <div className="max-w-[85%] rounded-xl px-3 py-2 text-sm bg-white border border-slate-200 text-slate-700">
        <div className="prose prose-sm max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-a:text-brand-teal">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
        {isStreaming && (
          <span className="inline-block w-1.5 h-4 bg-brand-teal animate-pulse ml-0.5 rounded-sm" />
        )}
        {/* Inline Sync Now button — replaces "go to IntelliHealth page" friction */}
        {showSyncButton && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-7 px-3 text-xs font-medium w-full',
                syncDone
                  ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                  : 'border-brand-teal text-brand-teal hover:bg-brand-teal/5',
              )}
              disabled={isSyncing || syncDone}
              onClick={handleSync}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  Syncing patient data…
                </>
              ) : syncDone ? (
                <>
                  ✓ Synced — ask your question again
                </>
              ) : (
                <>
                  <Database className="h-3 w-3 mr-1.5" />
                  Sync Patient Data Now
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
